import React, { useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Form } from 'react-bootstrap';
import { TbExclamationCircle } from 'react-icons/tb';
import styles from './CampaignCalendarPicker.module.scss';

const CampaignCalendarPicker = ({
  campaignStartDate,
  campaignEndDate,
  selectedDates = [],
  onChange,
  label,
  isRequired,
  helperText,
  iconWithLabel,
  minDates = 1,
  maxDates = null,
}) => {
  const [highlightDates, setHighlightDates] = useState(selectedDates);
  const [rangeStart, setRangeStart] = useState(null);
  const [isRangeMode, setIsRangeMode] = useState(false);

  // Calculate weeks starting from today (minimum 5 weeks shown)
  const campaignWeeks = useMemo(() => {
    if (!campaignStartDate || !campaignEndDate) return [];

    const weeks = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const campaignStart = new Date(campaignStartDate);

    // Always start from today if campaign already started
    const effectiveStart = today > campaignStart ? today : campaignStart;
    let currentDate = new Date(effectiveStart);

    // Always show at least 5 weeks
    const minWeeks = 5;

    for (let weekNumber = 1; weekNumber <= minWeeks; weekNumber++) {
      const weekStart = new Date(currentDate);
      const weekEnd = new Date(currentDate);
      weekEnd.setDate(weekEnd.getDate() + 6);

      weeks.push({
        weekNumber,
        start: weekStart,
        end: weekEnd,
        label: `Week ${weekNumber}`,
      });

      currentDate.setDate(currentDate.getDate() + 7);
    }

    return weeks;
  }, [campaignStartDate, campaignEndDate]);

  const handleDateChange = (date) => {
    if (!date) return;

    if (isRangeMode) {
      // Range selection mode
      if (!rangeStart) {
        // First click - set range start
        setRangeStart(date);
        setHighlightDates([date]);
        onChange([date]);
      } else {
        // Second click - complete the range
        const start = new Date(rangeStart);
        const end = new Date(date);

        // Ensure start is before end
        const [actualStart, actualEnd] = start <= end ? [start, end] : [end, start];

        // Generate all dates between start and end
        const datesInRange = [];
        const currentDate = new Date(actualStart);

        while (currentDate <= actualEnd) {
          datesInRange.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }

        setHighlightDates(datesInRange);
        onChange(datesInRange);
        setRangeStart(null); // Reset for next range selection
      }
    } else {
      // Individual date selection mode
      const dateStr = date.toDateString();
      const isAlreadySelected = highlightDates.some(
        (d) => d.toDateString() === dateStr
      );

      let newDates;
      if (isAlreadySelected) {
        // Remove date if already selected
        newDates = highlightDates.filter((d) => d.toDateString() !== dateStr);
      } else {
        // Add date if not selected (respect maxDates limit)
        if (maxDates && highlightDates.length >= maxDates) {
          return; // Don't add if max limit reached
        }
        newDates = [...highlightDates, date];
      }

      setHighlightDates(newDates);
      onChange(newDates);
    }
  };

  const handleWeekSelect = (week) => {
    const datesInWeek = [];
    let currentDate = new Date(week.start);
    const endDate = new Date(week.end);

    while (currentDate <= endDate) {
      datesInWeek.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Check if all dates in week are already selected
    const allSelected = datesInWeek.every((date) =>
      highlightDates.some((d) => d.toDateString() === date.toDateString())
    );

    let newDates;
    if (allSelected) {
      // Remove all dates in week
      newDates = highlightDates.filter(
        (d) =>
          !datesInWeek.some((wd) => wd.toDateString() === d.toDateString())
      );
    } else {
      // Add all dates in week that aren't already selected
      const datesToAdd = datesInWeek.filter(
        (date) =>
          !highlightDates.some((d) => d.toDateString() === date.toDateString())
      );
      newDates = [...highlightDates, ...datesToAdd];
    }

    setHighlightDates(newDates);
    onChange(newDates);
  };

  const clearSelection = () => {
    setHighlightDates([]);
    onChange([]);
  };

  const isDateInCampaign = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const campaignStart = new Date(campaignStartDate);
    // Use the later of today or campaign start (can't select past dates)
    const minSelectableDate = today > campaignStart ? today : campaignStart;
    return date >= minSelectableDate;
  };

  return (
    <Form.Group className={styles.calendarPickerWrapper}>
      {label && (
        <Form.Label className="Input_label">
          {`${label} `}
          {iconWithLabel && (
            <TbExclamationCircle
              color="#98A2B3"
              style={{
                height: '20px',
                width: '20px',
                marginLeft: '4px',
                transform: 'rotate(180deg)',
              }}
            />
          )}
          {isRequired && <span style={{ color: '#b42318' }}>*</span>}
        </Form.Label>
      )}

      {helperText && <div className={styles.helperText}>{helperText}</div>}

      <div className={styles.calendarContainer}>
        {/* Top controls: Selection mode toggle and Clear All */}
        <div className={styles.topControls}>
          <div className={styles.selectionModeToggle}>
            <button
              type="button"
              className={`${styles.modeButton} ${!isRangeMode ? styles.active : ''}`}
              onClick={() => {
                setIsRangeMode(false);
                setRangeStart(null);
              }}
            >
              Individual Dates
            </button>
            <button
              type="button"
              className={`${styles.modeButton} ${isRangeMode ? styles.active : ''}`}
              onClick={() => {
                setIsRangeMode(true);
                setRangeStart(null);
              }}
            >
              Date Range
            </button>
          </div>
          {highlightDates.length > 0 && (
            <button
              type="button"
              className={styles.clearAllButton}
              onClick={clearSelection}
            >
              Clear All
            </button>
          )}
        </div>

        {isRangeMode && rangeStart && (
          <div className={styles.rangeHelper}>
            Range start: {rangeStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            <br />
            Click an end date to complete the range
          </div>
        )}

        {/* Week selector buttons */}
        {campaignWeeks.length > 0 && !isRangeMode && (
          <div className={styles.weekSelector}>
            <div className={styles.weekSelectorHeader}>Quick Select by Campaign Week:</div>
            <div className={styles.weekButtons}>
              {campaignWeeks.map((week) => {
                const datesInWeek = [];
                let currentDate = new Date(week.start);
                const endDate = new Date(week.end);

                while (currentDate <= endDate) {
                  datesInWeek.push(new Date(currentDate));
                  currentDate.setDate(currentDate.getDate() + 1);
                }

                const allSelected = datesInWeek.every((date) =>
                  highlightDates.some(
                    (d) => d.toDateString() === date.toDateString()
                  )
                );

                return (
                  <button
                    key={week.weekNumber}
                    type="button"
                    className={`${styles.weekButton} ${
                      allSelected ? styles.selected : ''
                    }`}
                    onClick={() => handleWeekSelect(week)}
                  >
                    {week.label}
                    <span className={styles.weekDates}>
                      {week.start.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}{' '}
                      -{' '}
                      {week.end.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Calendar picker */}
        <div className={styles.datepickerContainer}>
          <style>{`
            .react-datepicker {
              gap: 32px !important;
              column-gap: 32px !important;
            }
            .react-datepicker__month-container + .react-datepicker__month-container {
              margin-left: 24px !important;
            }
          `}</style>
          <DatePicker
            inline
            selected={null}
            onChange={handleDateChange}
            highlightDates={highlightDates}
            minDate={new Date() > new Date(campaignStartDate) ? new Date() : new Date(campaignStartDate)}
            openToDate={new Date()}
            monthsShown={2}
            dayClassName={(date) => {
              const isSelected = highlightDates.some(
                (d) => d.toDateString() === date.toDateString()
              );
              const isInCampaign = isDateInCampaign(date);
              const isRangeStartDate = rangeStart && date.toDateString() === rangeStart.toDateString();

              // Check if date is in a range (between two selected dates)
              const sortedDates = [...highlightDates].sort((a, b) => a - b);
              let isInRange = false;
              let isRangeEnd = false;
              let isRangeStartInSelection = false;

              if (sortedDates.length > 1) {
                const firstDate = sortedDates[0];
                const lastDate = sortedDates[sortedDates.length - 1];
                isInRange = date > firstDate && date < lastDate && isSelected;
                isRangeEnd = date.toDateString() === lastDate.toDateString();
                isRangeStartInSelection = date.toDateString() === firstDate.toDateString();
              }

              if (!isInCampaign) {
                return styles.outsideCampaign;
              }

              if (isRangeStartDate) {
                return styles.rangeStartDate;
              }

              if (isRangeStartInSelection && sortedDates.length > 1) {
                return styles.rangeStartDate;
              }

              if (isRangeEnd && sortedDates.length > 1) {
                return styles.rangeEndDate;
              }

              if (isInRange) {
                return styles.inRangeDate;
              }

              if (isSelected) {
                return styles.selectedDate;
              }

              return styles.availableDate;
            }}
            calendarClassName={styles.calendar}
          />
        </div>
      </div>
    </Form.Group>
  );
};

export default CampaignCalendarPicker;
